import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, TURNSTILE_SITE_KEY } from "@/config";
import { Mail, Smartphone, ArrowLeft, Shield, Key } from "lucide-react";

const AdminLoginModal = ({ onClose, onSuccess }) => {
  const urlParams = new URLSearchParams(window.location.search);
  const showLegacy = urlParams.get('legacy') === 'true';

  const [step, setStep] = useState(showLegacy ? 'legacy' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaMethod, setMfaMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);
  const turnstileContainerRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || (step !== 'login' && step !== 'legacy')) return;
    const timeoutId = setTimeout(() => {
      if (!window.turnstile || !turnstileContainerRef.current) return;
      if (turnstileWidgetId !== null) {
        try { window.turnstile.remove(turnstileWidgetId); } catch (e) {}
      }
      try {
        const widgetId = window.turnstile.render(turnstileContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          theme: 'light'
        });
        setTurnstileWidgetId(widgetId);
      } catch (e) {}
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [step]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || window.turnstile) return;
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken('');
    if (window.turnstile && turnstileWidgetId !== null) {
      try { window.turnstile.reset(turnstileWidgetId); } catch (e) {}
    }
  }, [turnstileWidgetId]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password, turnstile_token: turnstileToken });
      if (res.data.requires_mfa) {
        setEmail(res.data.email || email);
        setMfaMethod(res.data.mfa_method);
        setStep('mfa');
        if (res.data.mfa_method === 'email') toast.success('Code sent to your email');
      } else if (res.data.token) {
        localStorage.setItem('adminToken', res.data.token);
        
        
        localStorage.setItem('adminInfo', JSON.stringify(res.data.user || {}));
        onSuccess(res.data.token);
        toast.success(`Welcome, ${res.data.user?.name || res.data.user?.email || 'Admin'}!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
      resetTurnstile();
    }
    setLoading(false);
  };

  const handleLegacyLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email: 'legacy', password, turnstile_token: turnstileToken });
      if (res.data.token) {
        localStorage.setItem('adminToken', res.data.token);
        
        
        localStorage.setItem('adminInfo', JSON.stringify(res.data.user || {}));
        onSuccess(res.data.token);
        toast.success(`Welcome, ${res.data.user?.name || res.data.user?.email || 'Admin'}!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid password');
      resetTurnstile();
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm">
      <div className="bg-white border border-gray-200 p-8 w-full max-w-md mx-4 rounded-lg shadow-lg">
        {step === 'login' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <Shield className="text-blue-600" size={28} />
              <h2 className="text-2xl font-bold text-gray-900">Admin Login</h2>
            </div>
            <form onSubmit={handleLogin}>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full bg-gray-50 border border-gray-200 px-4 py-3 mb-4 focus:border-blue-600 outline-none text-gray-900 rounded" required />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full bg-gray-50 border border-gray-200 px-4 py-3 mb-4 focus:border-blue-600 outline-none text-gray-900 rounded" required />
              {TURNSTILE_SITE_KEY && <div ref={turnstileContainerRef} className="mb-4 flex justify-center" />}
              <div className="flex gap-4">
                <button type="button" onClick={onClose} className="flex-1 bg-gray-200 text-gray-900 hover:bg-gray-300 py-3 rounded font-medium">Cancel</button>
                <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white hover:bg-blue-700 py-3 rounded font-medium">{loading ? 'Logging in...' : 'Login'}</button>
              </div>
            </form>
          </>
        )}
        {step === 'legacy' && (
          <>
            <button onClick={() => setStep('login')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"><ArrowLeft size={16} />Back</button>
            <div className="flex items-center gap-3 mb-6">
              <Key className="text-blue-600" size={28} />
              <h2 className="text-xl font-bold text-gray-900">Legacy Login</h2>
            </div>
            <form onSubmit={handleLegacyLogin}>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full bg-gray-50 border border-gray-200 px-4 py-3 mb-4 focus:border-blue-600 outline-none text-gray-900 rounded" autoFocus required />
              {TURNSTILE_SITE_KEY && <div ref={turnstileContainerRef} className="mb-4 flex justify-center" />}
              <div className="flex gap-4">
                <button type="button" onClick={() => setStep('login')} className="flex-1 bg-gray-200 text-gray-900 hover:bg-gray-300 py-3 rounded font-medium">Cancel</button>
                <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white hover:bg-blue-700 py-3 rounded font-medium">{loading ? 'Logging in...' : 'Login'}</button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminLoginModal;
